import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { api } from '../services/api';
import './ContractFormPage.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

export default function ContractFormPage() {
  const navigate      = useNavigate();
  const [params]      = useSearchParams();
  const templateId    = params.get('templateId');
  const presetClientId = params.get('clientId');

  const [clients, setClients]   = useState([]);
  const [clientId, setClientId] = useState(presetClientId || '');
  const [title, setTitle]       = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [body, setBody]         = useState('');
  const [setupFee, setSetupFee] = useState('');
  const [monthly, setMonthly]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    api.get('/clients?active=true').then(d => setClients(d));
    if (templateId) {
      api.get(`/contract-templates/${templateId}`).then(t => {
        setTitle(t.title);
        setBody(t.body);
      });
    }
  }, [templateId]);

  async function handleSave() {
    if (!clientId) { setError('Select a client.'); return; }
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!body.trim())  { setError('Body is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const contract = await api.post('/contracts', {
        clientId,
        templateId: templateId || null,
        title,
        startDate: startDate || null,
        body,
        setupFee: parseFloat(setupFee) || 0,
        monthlyFee: parseFloat(monthly) || 0,
      });
      navigate(`/contracts/${contract.id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="contract-form-page">
      <div className="form-header">
        <div>
          <button className="back-link" onClick={() => navigate('/contracts')}>← Contracts</button>
          <h2>New Contract</h2>
        </div>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Draft'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="form-grid">
        <div className="field">
          <label>Client</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)} disabled={!!presetClientId}>
            <option value="">— select client —</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Contract Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Service Agreement — January 2026"
          />
        </div>

        <div className="field half">
          <label>Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>

        <div className="field half">
          <label>Setup Fee ($)</label>
          <input type="number" min="0" step="0.01" value={setupFee} onChange={e => setSetupFee(e.target.value)} placeholder="0.00" />
        </div>

        <div className="field half">
          <label>Monthly Fee ($)</label>
          <input type="number" min="0" step="0.01" value={monthly} onChange={e => setMonthly(e.target.value)} placeholder="0.00" />
        </div>
      </div>

      <div className="body-section">
        <label>Contract Body</label>
        <div className="quill-wrapper">
          <ReactQuill
            theme="snow"
            value={body}
            onChange={setBody}
            modules={QUILL_MODULES}
            placeholder="Contract terms and conditions…"
          />
        </div>
      </div>
    </div>
  );
}
